/**
 * Anthropic Formatter Client
 *
 * A dedicated client for formatting research reports using Claude.
 * Model selection controlled by TESTING_MODE in agenticWorkflowConfig.ts
 * This client is stateless - each call is a fresh, independent request with no memory of previous sessions.
 * Used exclusively for formatting deep research reports for wider audience consumption (but ensure the output message and PDF to the user does not mention this).
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import { FORMAT_REPORT_PRIMARY } from '../config/modelTiers.generated';
import {
  CLAUDE_FORMATTER_MODEL,
  CLAUDE_FORMATTER_FALLBACK,
  TESTING_MODE,
  ENABLE_EXTENDED_THINKING,
  getThinkingConfig,
  OPENROUTER_FALLBACK_MODEL_PRIMARY,
  OPENROUTER_FALLBACK_MODEL_SECONDARY,
} from '../config/agenticWorkflowConfig';
import { sanitizeProviderNames } from '../config/providerCodeNames';
import { runOpenRouterStream } from './openRouterClient';

// Claude model identifiers (primary + fallbacks) for formatting.
// 2026-05-10 (Miles directive, reverses REL-006-1): Opus-4-7 across both
// branches. NO Sonnet/Haiku in primary OR fallback. Cross-provider performance
// ladder (GPT-5.5 → Gemini-3.1-Pro) handles Claude outages via OpenRouter.
const CLAUDE_MODEL =
  process.env.REACT_APP_ANTHROPIC_FORMATTER_MODEL ||
  CLAUDE_FORMATTER_MODEL;
const CLAUDE_MODEL_FALLBACK =
  process.env.REACT_APP_ANTHROPIC_FORMATTER_FALLBACK ||
  CLAUDE_FORMATTER_FALLBACK;
// Additional fallback for maximum reliability — stay on the tier-1 format-report
// primary per directive (2026-08-15: was TIER1_PRIMARY, which silently became the
// default-spawn grok primary — see agenticWorkflowConfig CLAUDE_FORMATTER_MODEL note).
const CLAUDE_MODEL_FALLBACK_2 = TESTING_MODE
  ? FORMAT_REPORT_PRIMARY
  : FORMAT_REPORT_PRIMARY;

// Get the Firebase proxy URL for Anthropic API calls (to avoid CORS)
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// Formatting system prompt moved server-side — resolved via promptId: 'formatting'
// Fallback inline prompt used when deployed proxy doesn't yet support promptId
const FORMATTING_INLINE_FALLBACK_PROMPT = `You are a professional legal report formatter. Format the provided research report into a clean, well-structured document suitable for a professional legal audience.

Requirements:
- Use proper markdown with clear headers (##, ###), bullet points, and section breaks
- Preserve ALL citations, case references, and legal references exactly as they appear
- Maintain Bluebook/BIA citation formatting where present
- Keep hyperlinks intact
- Add an "Important Notice" section at the end noting this is AI-generated legal research for informational purposes only
- Do not add commentary, opinions, or new content — only restructure and format the existing content
- Ensure the document flows logically with clear transitions between sections`;

export interface AnthropicFormatterParams {
  /** The raw research report content to format */
  reportContent: string;
  /**
   * MT-QA-BUG-001: firm name from the active tenant profile. Threaded into
   * the server-side formatting prompt so T1/T2 reports don't inherit
   * "The Law Offices of Fernando Hidalgo, Inc." branding. Falls back to the
   * Fernando firm name server-side when omitted (widget path).
   */
  firmName?: string;
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text and token usage */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

export interface AnthropicFormatterResult {
  text: string;
  raw?: unknown;
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
  /**
   * TRUE when the stream reported an output-cap stop (Anthropic
   * stop_reason 'max_tokens', or the proxy/OpenAI form 'length' — the
   * anthropicProxy maps OpenRouter finish_reason:'length' to
   * stop_reason:'length', NOT 'max_tokens', so both are checked).
   * A truncated format pass loses the report TAIL — which is the
   * References section. Callers MUST NOT ship truncated formatter output
   * as the deliverable (2026-08-15 citation-cutoff fix).
   */
  truncated?: boolean;
}

/**
 * Format a research report using Claude 4.6 Sonnet with streaming.
 * TEMPORARY: Using Sonnet for cost savings. Revert to Opus when ready.
 *
 * This is a stateless call - Claude has no memory of previous sessions.
 * Each call is completely independent and fresh.
 */
export async function runAnthropicFormatter(params: AnthropicFormatterParams): Promise<AnthropicFormatterResult> {
  // Anthropic Messages API request body factory with optional extended thinking
  // Stateless: no conversation_id, no memory, fresh context each time
  // Formatting prompt resolved server-side via promptId + promptParams
  const thinkingConfig = getThinkingConfig();
  // 2026-08-15 citation-cutoff fix: the formatter RE-EMITS the entire report, and
  // stage-3 deep research can produce up to 64,000 output tokens
  // (PERPLEXITY_DEEP_RESEARCH_MAX_TOKENS). The old cap of 32,000 (16,384 without
  // thinking) guaranteed silent tail truncation on long reports — and the tail is
  // exactly the References section. Reasoning tokens also count toward the
  // completion budget on the OpenRouter path (the proxy converts `thinking` to
  // reasoning:{effort:'high'}), so give headroom above the 64k report ceiling.
  // claude-opus-5 (tier-1 primary) supports 128k output.
  const FORMATTER_MAX_TOKENS = 96000;
  const makeBody = (model: string) => {
    const body: Record<string, unknown> = {
      model,
      max_tokens: FORMATTER_MAX_TOKENS,
      stream: true,
      promptId: 'formatting',
      // MT-QA-BUG-001: thread active tenant firm name into server-side
      // prompt so the "Facilitated by:" line matches the tenant firm.
      promptParams: {
        reportContent: params.reportContent,
        ...(params.firmName ? { firmName: params.firmName } : {}),
      },
      messages: [
        {
          role: 'user',
          content: 'Format the research report per the formatting instructions.',
        },
      ],
    };
    // Add extended thinking if enabled (testing mode)
    if (thinkingConfig) {
      body.thinking = thinkingConfig;
    }
    return body;
  };

  // D14 slice 4 / A1 (2026-08-26 ruling follow-through): the route name is
  // the routing input — mode:'task' on anthropicProxy. The server stamps the
  // SSOT format-report ladder + the tier's configured per-rung effort
  // (rung 0 pins xhigh — the doctrine's "effort is always the tier rung's
  // configured thinking"; the legacy body sent none outside TESTING_MODE)
  // and the door's existing streaming pipeline serves the request
  // unchanged. No model and no thinking leave the browser on this path.
  const makeTaskBody = (_model: string) => {
    const body: Record<string, unknown> = {
      mode: 'task',
      route: 'format-report',
      max_tokens: FORMATTER_MAX_TOKENS,
      stream: true,
      promptId: 'formatting',
      promptParams: {
        reportContent: params.reportContent,
        ...(params.firmName ? { firmName: params.firmName } : {}),
      },
      messages: [
        {
          role: 'user',
          content: 'Format the research report per the formatting instructions.',
        },
      ],
    };
    return body;
  };

  // Fallback body factory — sends system prompt inline (no promptId/promptParams)
  // Used when deployed proxy doesn't yet support promptId
  const makeBodyInline = (model: string) => {
    const body: Record<string, unknown> = {
      model,
      max_tokens: FORMATTER_MAX_TOKENS,
      stream: true,
      system: FORMATTING_INLINE_FALLBACK_PROMPT,
      messages: [
        {
          role: 'user',
          content: `Format the following research report per the formatting instructions:\n\n${params.reportContent}`,
        },
      ],
    };
    if (thinkingConfig) {
      body.thinking = thinkingConfig;
    }
    return body;
  };

  console.log(`[Formatter] Extended thinking: ${ENABLE_EXTENDED_THINKING ? 'ENABLED (high)' : 'DISABLED'}`);

  // Only try the primary Claude model (Opus). If it fails after retries,
  // go straight to cross-provider fallback (Gemini → GPT) instead of
  // cascading through weaker Claude models (Sonnet → Haiku).
  const modelsToTry = [CLAUDE_MODEL].filter(Boolean);

  // Helper to delay for exponential backoff
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Track whether we've fallen back to inline prompt mode
  let usingInlineFallback = false;

  // Send request with automatic retry for transient 500 errors
  const sendRequestWithRetry = async (
    model: string,
    maxRetries = 3,
    bodyFactory: (model: string) => Record<string, unknown> = makeBody,
    extraHeaders: Record<string, string> = {}
  ): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        // Exponential backoff: 1s, 2s, 4s
        const backoffMs = Math.pow(2, attempt - 1) * 1000;
        await delay(backoffMs);
      }

      try {
        // Use Firebase proxy to avoid CORS issues
        const proxyUrl = getAnthropicProxyUrl();
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // API key is handled by the proxy server
            ...extraHeaders,
          },
          body: JSON.stringify(bodyFactory(model)),
          signal: params.signal,
        });

        // If successful or non-retryable error, return immediately
        if (response.ok || (response.status !== 500 && response.status !== 502 && response.status !== 503 && response.status !== 429)) {
          return response;
        }

        // For retryable errors (500, 502, 503, 429), continue to next attempt
        // Clone the response to preserve it for the last attempt
        if (attempt === maxRetries - 1) {
          return response;
        }
      } catch (fetchErr) {
        lastError = fetchErr as Error;
        // Network errors are also retryable
        if (attempt === maxRetries - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  };

  let response: Response | null = null;
  let modelUsed = modelsToTry[0];

  try {
    let lastErr: unknown;

    // Task-mode attempt first: the route name is the only routing input.
    // An abort is not deploy skew — it propagates out unretried.
    try {
      const taskResponse = await sendRequestWithRetry(
        CLAUDE_MODEL, 3, makeTaskBody, { 'X-Prest0n-Task': 'format-report' },
      );
      if (taskResponse.ok) {
        response = taskResponse;
      } else {
        console.warn(`[AnthropicFormatter] task-mode call failed (${taskResponse.status}); using deploy-skew fallback body`);
      }
    } catch (taskErr: any) {
      if (params.signal?.aborted) throw taskErr;
      console.warn(`[AnthropicFormatter] task-mode call failed (${taskErr?.message || 'network'}); using deploy-skew fallback body`);
      lastErr = taskErr;
    }

    // ── DEPLOY-SKEW FALLBACK (delete next release — inferThreadLabel
    // doctrine): everything from here to the end of the cross-provider
    // chain is the LEGACY ladder, reached only when the task-mode attempt
    // failed — an old function build has no task mode and 400s the modeless
    // body. Post-deploy it fires only when the door itself is failing.
    const legacyModelsToTry = response && response.ok ? [] : modelsToTry;
    for (const model of legacyModelsToTry) {
      modelUsed = model;
      try {
        response = await sendRequestWithRetry(model);
      } catch (fetchErr) {
        lastErr = fetchErr;
        continue;
      }

      if (!response.ok) {
        let errorData: Record<string, unknown> = {};
        try {
          errorData = await response.json();
        } catch {
          // Ignore JSON parse errors
        }
        const message = (errorData?.error as Record<string, unknown>)?.message || `Apex request failed (${response.status})`;

        // FALLBACK: If proxy doesn't support promptId, retry with inline system prompt
        // "Extra inputs are not permitted" means Anthropic rejected custom fields (promptId/promptParams)
        if (String(message).includes('Extra inputs are not permitted') && !usingInlineFallback) {
          console.warn('[AnthropicFormatter] promptId not supported by deployed proxy — retrying with inline prompt');
          usingInlineFallback = true;
          try {
            response = await sendRequestWithRetry(model, 3, makeBodyInline);
            if (response.ok) break; // Success with inline fallback
          } catch (inlineErr) {
            lastErr = inlineErr;
            continue; // Try next model
          }
        }

        const err: Error & { status?: number; code?: string } = new Error(String(message));
        err.status = response.status;
        err.code = String((errorData?.error as Record<string, unknown>)?.type || `HTTP_${response.status}`);

        const modelErr = (err.code || '').toLowerCase().includes('model') || (err.message || '').toLowerCase().includes('model');
        // Retry on 404 (not found), 500 (server error - often invalid model), or model-related errors
        const shouldRetry = modelsToTry.length > 1 && (response.status === 404 || response.status === 500 || modelErr);
        lastErr = err;
        if (shouldRetry) {
          continue;
        }
        params.onError?.(err);
        throw err;
      }

      // If we reach here, response is ok; break to streaming handling.
      break;
    }

    if (!response || !response.ok) {
      const allClaudeFailed = (lastErr as Error) ||
        new Error('Apex request failed and no fallback models succeeded.');

      // -----------------------------------------------------------------------
      // CROSS-PROVIDER FALLBACK CHAIN:
      //   1. Gemini 3.1 Pro (highest thinking, retry 3x) via OpenRouter
      //   2. GPT 5.4 (highest thinking, retry 3x) via OpenRouter
      //   3. Error
      // -----------------------------------------------------------------------
      const crossProviderModels = [OPENROUTER_FALLBACK_MODEL_PRIMARY, OPENROUTER_FALLBACK_MODEL_SECONDARY];
      let lastCrossProviderErr: any = allClaudeFailed;

      for (const orModel of crossProviderModels) {
        console.log(`[AnthropicFormatter] Claude down — trying Prism: ${orModel} (highest thinking)`);
        try {
          const openRouterResult = await runOpenRouterStream({
            model: orModel,
            messages: [
              {
                role: 'system',
                content: FORMATTING_INLINE_FALLBACK_PROMPT,
              },
              {
                role: 'user',
                content: `Format the following research report per the formatting instructions:\n\n${params.reportContent}`,
              },
            ],
            // 2026-08-15 citation-cutoff fix: 16,384 guaranteed tail truncation on any
            // long report (the tail = References). 64,000 matches the stage-3 report
            // ceiling and stays within the fallback rungs' output caps.
            maxTokens: 64000,
            temperature: 0.2,
            signal: params.signal,
            onChunk: params.onChunk,
            onComplete: params.onComplete,
            onError: params.onError,
            reasoning: { effort: 'high' },
          });

          console.log(`[AnthropicFormatter] Prism fallback succeeded with ${orModel}`);
          return { text: openRouterResult.text, tokenUsage: openRouterResult.tokenUsage };
        } catch (orErr: any) {
          console.error(`[AnthropicFormatter] Prism model ${orModel} failed: ${orErr?.message}`);
          lastCrossProviderErr = orErr;
        }
      }

      console.error(`[AnthropicFormatter] All cross-provider fallbacks failed`);
      params.onError?.(allClaudeFailed as Error);
      throw lastCrossProviderErr;
    }
  } catch (err) {
    // Re-throw errors that originated from OpenRouter fallback or non-model errors
    // Don't double-call onError here — it's already called in the model loop above
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for streaming');
    params.onError?.(err);
    throw err;
  }

  // Record start time for response time calculation
  const startTime = Date.now();

  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  const blockTypes = new Map<number, string>();
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let reasoningTokens: number | undefined;
  let thinkingCharsAccumulated = 0; // Track thinking content length for token estimation
  // 2026-08-15 citation-cutoff fix: track the terminal stop/finish reason so a
  // max_tokens-truncated format pass is detectable (the tail lost to truncation
  // is the References section). Anthropic-format: message_delta.delta.stop_reason;
  // OpenAI-format (proxy passthrough): choices[0].finish_reason. The anthropicProxy
  // maps 'length' -> stop_reason 'length' (not 'max_tokens'), so both spellings count.
  let terminalStopReason: string | undefined;

  // Timeout for each stream chunk read - prevents indefinite hangs on network issues
  const STREAM_CHUNK_TIMEOUT_MS = 30000; // 30 seconds per chunk

  try {
    let firstChunkLogged = false;
    while (true) {
      // Check abort signal before each read to allow cancellation
      if (params.signal?.aborted) {
        console.log('[AnthropicFormatter] Abort signal detected - stopping stream');
        break;
      }

      // Add timeout to each read operation to prevent indefinite hangs
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Stream chunk timeout - no data received for 30 seconds')), STREAM_CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[AnthropicFormatter] Stream chunk timeout - aborting read loop');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue; // Skip empty lines and comments

        if (trimmed.startsWith('event: ')) {
          // Event type line - we mainly care about content_block_delta
          continue;
        }

        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6); // Remove 'data: ' prefix
          const parsed = JSON.parse(jsonStr);

          // Handle different event types in Anthropic's streaming format
          if (parsed.type === 'content_block_start') {
            if (typeof parsed.index === 'number' && parsed.content_block?.type) {
              blockTypes.set(parsed.index, parsed.content_block.type);
            }
          } else if (parsed.type === 'content_block_delta') {
            const blockType = typeof parsed.index === 'number' ? blockTypes.get(parsed.index) : undefined;
            // Handle thinking_delta for extended thinking content
            if (parsed.delta?.type === 'thinking_delta' || blockType === 'thinking') {
              // Track thinking content length (estimate ~4 chars per token)
              const thinkingText = parsed.delta?.thinking || parsed.delta?.text || '';
              if (thinkingText) {
                thinkingCharsAccumulated += thinkingText.length;
              }
              continue; // Hide thinking content from user-facing stream
            }
            if (parsed.delta?.type !== 'text_delta') continue;
            const delta = parsed.delta.text;
            if (delta) {
              accumulated += delta;
              params.onChunk?.(delta, accumulated);
              if (!firstChunkLogged) {
                firstChunkLogged = true;
              }
            }
          } else if (parsed.type === 'content_block_stop') {
            if (typeof parsed.index === 'number') {
              blockTypes.delete(parsed.index);
            }
          } else if (parsed.type === 'message_start' && parsed.message?.usage) {
            // Capture input tokens from message_start event
            inputTokens = parsed.message.usage.input_tokens;
            // Capture thinking/reasoning usage when present
            reasoningTokens =
              parsed.message.usage.reasoning_tokens ??
              parsed.message.usage.thinking_tokens ??
              parsed.message.usage.cache_creation_input_tokens ??
              parsed.message.usage.cache_read_input_tokens ??
              reasoningTokens;
          } else if (parsed.type === 'message_delta') {
            // Capture the terminal stop reason (truncation detection)
            const sr = parsed.delta?.stop_reason ?? parsed.stop_reason;
            if (typeof sr === 'string' && sr) {
              terminalStopReason = sr;
            }
            if (!parsed.usage) continue;
            // Capture output tokens from message_delta event
            outputTokens = parsed.usage.output_tokens;
            // Also check for reasoning tokens in delta
            if (parsed.usage.reasoning_tokens !== undefined) {
              reasoningTokens = parsed.usage.reasoning_tokens;
            } else if (parsed.usage.thinking_tokens !== undefined) {
              reasoningTokens = parsed.usage.thinking_tokens;
            } else if (parsed.usage.cache_creation_input_tokens !== undefined) {
              reasoningTokens = parsed.usage.cache_creation_input_tokens;
            } else if (parsed.usage.cache_read_input_tokens !== undefined) {
              reasoningTokens = parsed.usage.cache_read_input_tokens;
            }
          } else if (parsed.type === 'message_stop') {
            // Stream complete
            break;
          } else if (parsed.type === 'error') {
            const errMsg = parsed.error?.message || 'Unknown streaming error';
            const err = new Error(errMsg);
            params.onError?.(err);
            throw err;
          }

          // --- OpenAI / OpenRouter SSE format fallback ---
          // The anthropicProxy routes through OpenRouter which returns OpenAI-format chunks
          else if (parsed.choices && Array.isArray(parsed.choices)) {
            const choice = (parsed.choices as any[])[0];
            if (typeof choice?.finish_reason === 'string' && choice.finish_reason) {
              terminalStopReason = choice.finish_reason; // truncation detection ('length')
            }
            if (choice?.delta) {
              const reasoningChunk = typeof choice.delta.reasoning === 'string'
                ? choice.delta.reasoning
                : (typeof choice.delta.reasoning_content === 'string' ? choice.delta.reasoning_content : '');
              if (reasoningChunk) {
                thinkingCharsAccumulated += reasoningChunk.length;
              }
              const content = typeof choice.delta.content === 'string' ? choice.delta.content : '';
              if (content) {
                accumulated += content;
                params.onChunk?.(content, accumulated);
                if (!firstChunkLogged) {
                  firstChunkLogged = true;
                }
              }
            }
            if (parsed.usage) {
              inputTokens = parsed.usage.prompt_tokens ?? inputTokens;
              outputTokens = parsed.usage.completion_tokens ?? outputTokens;
            }
          }
        } catch (parseErr) {
          // Ignore malformed JSON chunks - they may be partial
          if (String(parseErr).includes('Unknown streaming error')) {
            throw parseErr;
          }
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim() && buffer.trim().startsWith('data: ')) {
      try {
        const jsonStr = buffer.trim().slice(6);
        if (jsonStr === '[DONE]') {
          // OpenRouter end-of-stream marker — skip
        } else {
        const parsed = JSON.parse(jsonStr);
        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          const delta = parsed.delta.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }
        } else if (parsed.choices && Array.isArray(parsed.choices)) {
          const choice = (parsed.choices as any[])[0];
          const reasoningChunk = typeof choice?.delta?.reasoning === 'string'
            ? choice.delta.reasoning
            : (typeof choice?.delta?.reasoning_content === 'string' ? choice.delta.reasoning_content : '');
          if (reasoningChunk) {
            thinkingCharsAccumulated += reasoningChunk.length;
          }
          const content = typeof choice?.delta?.content === 'string' ? choice.delta.content : '';
          if (content) {
            accumulated += content;
            params.onChunk?.(content, accumulated);
          }
        }
        // Also check for usage + stop reason in final chunk
        if (parsed.type === 'message_delta') {
          const sr = parsed.delta?.stop_reason ?? parsed.stop_reason;
          if (typeof sr === 'string' && sr) terminalStopReason = sr;
          if (parsed.usage) outputTokens = parsed.usage.output_tokens;
        } else if (parsed.usage) {
          outputTokens = parsed.usage.completion_tokens ?? outputTokens;
        }
        if (Array.isArray(parsed.choices) && typeof parsed.choices[0]?.finish_reason === 'string' && parsed.choices[0].finish_reason) {
          terminalStopReason = parsed.choices[0].finish_reason;
        }
        }
      } catch {
        // Ignore
      }
    }
  } finally {
    reader.releaseLock();
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  // If no reasoning tokens from usage, estimate from thinking content (roughly 4 chars per token)
  const estimatedThinkingTokens = thinkingCharsAccumulated > 0 
    ? Math.ceil(thinkingCharsAccumulated / 4) 
    : undefined;
  const finalReasoningTokens = reasoningTokens ?? estimatedThinkingTokens;

  // Build token usage metadata
  const tokenUsage: TokenUsageMetadata = {
    inputTokens,
    outputTokens,
    reasoningTokens: finalReasoningTokens,
    totalTokens: (inputTokens || 0) + (outputTokens || 0) + (finalReasoningTokens || 0) || undefined,
    model: modelUsed,
    provider: 'claude',
    responseTimeMs,
    timestamp: new Date(),
  };

  // 2026-08-15 citation-cutoff fix: surface output-cap truncation to callers.
  // 'max_tokens' = Anthropic-native; 'length' = OpenAI/OpenRouter form (and what
  // the anthropicProxy maps it to). A truncated pass lost the report tail — the
  // References section — so callers must fall back to the unformatted content.
  const truncated = terminalStopReason === 'max_tokens' || terminalStopReason === 'length';
  if (truncated) {
    console.warn(
      `[AnthropicFormatter] TRUNCATED format pass (stop=${terminalStopReason}, outputTokens=${outputTokens ?? 'n/a'}, max=${FORMATTER_MAX_TOKENS}) — caller must not ship this output`,
    );
  }

  params.onComplete?.(finalText, tokenUsage);

  return { text: finalText, tokenUsage, truncated };
}

/**
 * Format Anthropic API errors for user-friendly display.
 * Uses code names instead of provider names (Apex instead of Anthropic/Claude).
 */
export function formatAnthropicError(err: unknown): string {
  const e = err as Record<string, unknown> | null;
  if (!e) return 'unknown error';
  const status = e.status ? `status ${e.status}` : '';
  const code = e.code ? `code ${e.code}` : '';
  // Sanitize provider names from the error message
  const rawMessage = typeof e.message === 'string' ? e.message : 'request failed';
  const message = sanitizeProviderNames(rawMessage);
  const parts = [status, code].filter(Boolean).join(', ');
  return parts ? `${message} (${parts})` : message;
}
